import { useState, useMemo, useCallback, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Plus, Upload, X } from 'lucide-react'
import type { VendorsListResponse, VendorListItem, ValidityStatus } from '@common/types'
import { VALIDITY_STATUSES } from '@common/types'
import { calculateValidityStatus } from '@common/utils/validity'
import { useFilters, useSort, type FilterDefinition, type SortColumnDef } from '@frontend/lib/filters'
import { FilterBar } from '@frontend/components/filters/FilterBar'
import { SearchFilter } from '@frontend/components/filters/SearchFilter'
import { MultiSelect } from '@frontend/components/ui/multi-select'
import { api } from '@frontend/lib/api'
import { usePermissions } from '@frontend/hooks/usePermissions'
import { formatDate } from '@frontend/lib/utils'
import { AppLayout } from '@frontend/components/layout/AppLayout'
import { Button } from '@frontend/components/ui/button'
import { Card, CardContent } from '@frontend/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  SortableTableHead,
} from '@frontend/components/ui/table'
import { Spinner } from '@frontend/components/ui/spinner'
import { Checkbox } from '@frontend/components/ui/checkbox'
import { VendorValidityBadge } from '@frontend/components/ui/status-badge'
import { VendorModal } from '@frontend/components/vendors/VendorModal'
import { BulkUploadToClientsDialog } from '@frontend/components/vendors/BulkUploadToClientsDialog'

async function fetchVendors(): Promise<VendorsListResponse> {
  return api.get('vendors').json<VendorsListResponse>()
}

// Frontend-only status that layers bridge-letter coverage on top of the shared
// ValidityStatus: an expired report kept usable by a currently-valid bridge
// letter reads as "Bridge Letter", not "Invalid".
type VendorValidityFilter = ValidityStatus | 'bridge_covered'

const VALIDITY_STATUS_LABELS: Record<VendorValidityFilter, string> = {
  valid: 'Valid',
  caution: 'Caution',
  invalid: 'Invalid',
  unknown: 'Unknown',
  bridge_covered: 'Bridge Letter',
}

const VALIDITY_STATUS_OPTIONS: { value: VendorValidityFilter; label: string }[] = [
  ...VALIDITY_STATUSES.map((status) => ({ value: status, label: VALIDITY_STATUS_LABELS[status] })),
  { value: 'bridge_covered', label: VALIDITY_STATUS_LABELS.bridge_covered },
]

interface EnrichedVendor extends VendorListItem {
  validityStatus: VendorValidityFilter
}

function isVendorSelectable(vendor: EnrichedVendor): boolean {
  // A currently-valid bridge letter keeps an otherwise-expired (day 90+) latest
  // review uploadable, matching the server-side bulk gate (spec: days 90–180
  // with a valid bridge letter remain uploadable).
  return vendor.validityStatus === 'valid' || vendor.validityStatus === 'caution' || vendor.bridgeLetterValid
}

export function Vendors() {
  const { can } = usePermissions()
  const [addVendorOpen, setAddVendorOpen] = useState(false)
  const [selectedVendorIds, setSelectedVendorIds] = useState<Set<string>>(new Set())
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['vendors'],
    queryFn: fetchVendors,
  })

  const enrichedVendors = useMemo<EnrichedVendor[]>(() => {
    return (data?.vendors ?? []).map((vendor) => {
      const validity = vendor.reviewCount === 0 ? null : calculateValidityStatus(vendor.latestAuditPeriodEnd)
      const base: VendorValidityFilter = validity?.status ?? 'unknown'
      // A currently-valid bridge letter over an *expired* report (day 0+, not just
      // the day-90+ "invalid" window) reads as "Bridge Letter" — matching what
      // VendorValidityBadge renders, so the badge and this filter can't disagree.
      const reportExpired = validity != null && validity.status !== 'unknown' && validity.daysRemaining <= 0
      const validityStatus: VendorValidityFilter = reportExpired && vendor.bridgeLetterValid ? 'bridge_covered' : base
      return { ...vendor, validityStatus }
    })
  }, [data?.vendors])

  const filterDefinitions = useMemo<FilterDefinition<EnrichedVendor>[]>(
    () => [
      {
        id: 'search',
        label: 'Search',
        type: 'search',
        match: (vendor, value) => {
          const search = value as string
          if (!search) return true
          const lower = search.toLowerCase()
          return (
            vendor.name.toLowerCase().includes(lower) ||
            vendor.aliases.some((alias) => alias.toLowerCase().includes(lower))
          )
        },
      },
      {
        id: 'validity',
        label: 'Validity',
        type: 'multiselect',
        options: VALIDITY_STATUS_OPTIONS,
        match: (vendor, value) => {
          const validities = value as VendorValidityFilter[]
          if (validities.length === 0) return true
          return validities.includes(vendor.validityStatus)
        },
      },
    ],
    []
  )

  const { filteredData, filterState, setFilter, clearAllFilters, activeFilterCount } = useFilters(
    enrichedVendors,
    filterDefinitions
  )

  const sortColumns = useMemo<SortColumnDef<EnrichedVendor>[]>(
    () => [
      { id: 'name', accessor: (v) => v.name },
      { id: 'reviews', accessor: (v) => v.reviewCount },
      { id: 'created', accessor: (v) => v.createdAt },
    ],
    []
  )

  const { sortedData, toggleSort, isSorted } = useSort(filteredData, sortColumns, {
    field: 'name',
    direction: 'asc',
  })

  const searchValue = (filterState.search as string) || ''
  const validityValue = (filterState.validity as string[]) || []

  // Prune selected vendor IDs when filter/sort changes hide rows
  const visibleIds = useMemo(() => new Set(sortedData.map((v) => v.id)), [sortedData])
  useEffect(() => {
    setSelectedVendorIds((prev) => {
      const pruned = new Set([...prev].filter((id) => visibleIds.has(id)))
      return pruned.size === prev.size ? prev : pruned
    })
  }, [visibleIds])

  // Checkbox selection
  const selectableVendors = useMemo(() => sortedData.filter(isVendorSelectable), [sortedData])

  const allSelectableSelected =
    selectableVendors.length > 0 && selectableVendors.every((v) => selectedVendorIds.has(v.id))

  const someSelected = selectableVendors.some((v) => selectedVendorIds.has(v.id))

  const handleToggleVendor = useCallback((vendorId: string) => {
    setSelectedVendorIds((prev) => {
      const next = new Set(prev)
      if (next.has(vendorId)) {
        next.delete(vendorId)
      } else {
        next.add(vendorId)
      }
      return next
    })
  }, [])

  const handleToggleAll = useCallback(() => {
    if (allSelectableSelected) {
      setSelectedVendorIds(new Set())
    } else {
      setSelectedVendorIds(new Set(selectableVendors.map((v) => v.id)))
    }
  }, [allSelectableSelected, selectableVendors])

  // Build vendorNames map for the dialog
  const vendorNamesMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const vendor of enrichedVendors) {
      map.set(vendor.id, vendor.name)
    }
    return map
  }, [enrichedVendors])

  const selectedVendorIdsArray = useMemo(() => Array.from(selectedVendorIds), [selectedVendorIds])

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Vendors</h1>
            <p className="mt-1 text-muted-foreground">Manage vendors and their compliance reviews</p>
          </div>
          <div className="flex items-center gap-2">
            {selectedVendorIds.size > 0 && (
              <>
                <span className="text-sm text-muted-foreground">{selectedVendorIds.size} selected</span>
                <Button onClick={() => setBulkUploadOpen(true)}>
                  <Upload className="h-4 w-4 mr-1" />
                  Upload to Client
                </Button>
              </>
            )}
            {can('vendors.create') && (
              <Button
                variant={selectedVendorIds.size > 0 ? 'outline' : 'default'}
                onClick={() => setAddVendorOpen(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Vendor
              </Button>
            )}
          </div>
        </div>

        {/* Main content card */}
        <Card className="shadow-sm">
          <CardContent className="p-4">
            {/* Filters */}
            {!isLoading && !error && (
              <>
                <FilterBar>
                  <SearchFilter
                    value={searchValue}
                    onChange={(value) => setFilter('search', value)}
                    placeholder="Search vendors..."
                  />
                  <MultiSelect
                    options={VALIDITY_STATUS_OPTIONS}
                    selected={validityValue}
                    onChange={(value) => setFilter('validity', value)}
                    placeholder="Validity..."
                    searchPlaceholder="Search validity..."
                    emptyMessage="No options found."
                    className="w-36"
                  />
                  {activeFilterCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearAllFilters}
                      className="h-9 px-2 text-muted-foreground"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Clear filters
                    </Button>
                  )}
                </FilterBar>

                {/* Results count */}
                <div className="text-sm text-muted-foreground mt-4">
                  {sortedData.length} of {enrichedVendors.length} vendor{enrichedVendors.length !== 1 ? 's' : ''}
                  {activeFilterCount > 0 && ' (filtered)'}
                </div>
              </>
            )}

            {isLoading ? (
              <div className="flex justify-center py-16">
                <Spinner size="lg" />
              </div>
            ) : error ? (
              <div className="py-16 text-center">
                <p className="text-destructive">Failed to load vendors.</p>
                <p className="mt-1 text-sm text-muted-foreground">Please try refreshing the page.</p>
              </div>
            ) : sortedData.length === 0 ? (
              <div className="py-16 text-center">
                <p className="text-muted-foreground">
                  {activeFilterCount > 0 ? 'No vendors match your filters.' : 'No vendors found.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-[40px]">
                        <Checkbox
                          checked={allSelectableSelected ? true : someSelected ? 'indeterminate' : false}
                          onCheckedChange={handleToggleAll}
                          aria-label="Select all vendors with valid reports"
                        />
                      </TableHead>
                      <SortableTableHead
                        className="w-[200px]"
                        sorted={isSorted('name')}
                        onSort={() => toggleSort('name')}
                      >
                        Vendor
                      </SortableTableHead>
                      <SortableTableHead
                        className="w-[100px]"
                        sorted={isSorted('reviews')}
                        onSort={() => toggleSort('reviews')}
                      >
                        Reviews
                      </SortableTableHead>
                      <TableHead className="w-[180px]">Status</TableHead>
                      <SortableTableHead
                        className="w-[150px]"
                        sorted={isSorted('created')}
                        onSort={() => toggleSort('created')}
                      >
                        Created
                      </SortableTableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedData.map((vendor) => {
                      const selectable = isVendorSelectable(vendor)
                      return (
                        <TableRow key={vendor.id} className="group">
                          <TableCell>
                            <Checkbox
                              checked={selectedVendorIds.has(vendor.id)}
                              onCheckedChange={() => handleToggleVendor(vendor.id)}
                              disabled={!selectable}
                              aria-label={selectable ? `Select ${vendor.name}` : 'No valid approved report'}
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            <Link to={`/vendors/${vendor.id}`} className="hover:underline">
                              {vendor.name}
                            </Link>
                          </TableCell>
                          <TableCell>{vendor.reviewCount}</TableCell>
                          <TableCell>
                            <VendorValidityBadge
                              latestAuditPeriodEnd={vendor.latestAuditPeriodEnd}
                              reviewCount={vendor.reviewCount}
                              bridgeLetterEndDate={vendor.bridgeLetterEndDate}
                              bridgeLetterValid={vendor.bridgeLetterValid}
                            />
                          </TableCell>
                          <TableCell>{formatDate(vendor.createdAt)}</TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Vendor Modal */}
      <VendorModal mode="create" open={addVendorOpen} onOpenChange={setAddVendorOpen} />

      {/* Bulk Upload Modal */}
      <BulkUploadToClientsDialog
        vendorIds={selectedVendorIdsArray}
        vendorNames={vendorNamesMap}
        open={bulkUploadOpen}
        onOpenChange={setBulkUploadOpen}
        onComplete={() => setSelectedVendorIds(new Set())}
      />
    </AppLayout>
  )
}
